/**
 * Structured wiki blocks (`cards`, `steps`, `callout`) are fenced code blocks
 * whose body stays plain, readable markdown, so the page source is still the
 * content and search still indexes every word of it (z8nrz7f3kc):
 *
 *   ```cards
 *   ### CGT API
 *   icon: server
 *   badge: active
 *   NestJS REST + WebSocket API, on ECS
 *   ```
 *
 * Each `### ` line opens an item. `key: value` lines directly under it set the
 * item's attributes; the first other line starts its body, after which a
 * `key:` line is body text like any other.
 */

export const BLOCK_ATTRS = ['icon', 'badge', 'tone', 'meta', 'link', 'image', 'check'] as const
export type BlockAttr = (typeof BLOCK_ATTRS)[number]

export interface BlockItem {
  title: string
  attrs: Partial<Record<BlockAttr, string>>
  /** Inline markdown; lines joined with a space (with a newline in a ```guide). */
  body: string
}

export interface ParsedBlock {
  /** Lines before the first `### ` item, joined — a caption for the block. */
  intro: string
  items: BlockItem[]
}

const ATTR_LINE = new RegExp(`^(${BLOCK_ATTRS.join('|')}):\\s*(.*)$`)

/**
 * `multiline` keeps an item's line breaks and blank lines, for a ```guide,
 * whose step bodies are block markdown (lists, paragraphs) rather than inline.
 */
export function parseBlock(source: string, multiline = false): ParsedBlock {
  const intro: string[] = []
  const items: BlockItem[] = []
  let current: (BlockItem & { lines: string[]; inBody: boolean }) | null = null

  const flush = () => {
    if (!current) return
    items.push({ title: current.title, attrs: current.attrs, body: current.lines.join(multiline ? '\n' : ' ').trim() })
  }

  for (const raw of source.split('\n')) {
    const line = raw.trim()
    if (line.startsWith('### ')) {
      flush()
      current = { title: line.slice(4).trim(), attrs: {}, body: '', lines: [], inBody: false }
      continue
    }
    if (!current) {
      if (line) intro.push(line)
      continue
    }
    if (!line) {
      if (multiline && current.inBody) current.lines.push('')
      continue
    }
    const attr = current.inBody ? null : ATTR_LINE.exec(line)
    if (attr) {
      current.attrs[attr[1] as BlockAttr] = attr[2].trim()
      continue
    }
    current.inBody = true
    current.lines.push(line)
  }
  flush()
  return { intro: intro.join(' '), items }
}

/**
 * A ```setup block is a set of questions ("How do I set up storage?"), each a
 * guide of its own. `## ` opens a question; `key: value` lines under it set
 * its attributes, where `needs:` lists the ids of questions to finish first;
 * other lines up to the first `### ` are its intro, and the `### ` items after
 * that are its steps, read exactly as a ```guide.
 */
/** `short:` names the question where it is listed as another's prerequisite. */
export const TOPIC_ATTRS = ['id', 'short', 'icon', 'tone', 'meta', 'badge', 'needs', 'video'] as const
export type TopicAttr = (typeof TOPIC_ATTRS)[number]

export interface SetupTopic {
  id: string
  title: string
  attrs: Partial<Record<TopicAttr, string>>
  /** Ids from `needs:`, in the order written. */
  needs: string[]
  intro: string
  steps: BlockItem[]
}

const TOPIC_ATTR_LINE = new RegExp(`^(${TOPIC_ATTRS.join('|')}):\\s*(.*)$`)

export function parseSetup(source: string): { intro: string; topics: SetupTopic[] } {
  const intro: string[] = []
  const topics: SetupTopic[] = []
  let current: { title: string; attrs: SetupTopic['attrs']; head: string[]; rest: string[]; inIntro: boolean } | null = null

  const flush = () => {
    if (!current) return
    const id = current.attrs.id || current.title.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '')
    topics.push({
      id,
      title: current.title,
      attrs: current.attrs,
      needs: (current.attrs.needs ?? '').split(',').map(s => s.trim()).filter(Boolean),
      intro: current.head.join('\n').trim(),
      steps: parseBlock(current.rest.join('\n'), true).items,
    })
  }

  for (const raw of source.split('\n')) {
    const line = raw.trim()
    if (line.startsWith('## ')) {
      flush()
      current = { title: line.slice(3).trim(), attrs: {}, head: [], rest: [], inIntro: false }
      continue
    }
    if (!current) {
      if (line) intro.push(line)
      continue
    }
    if (current.rest.length > 0 || line.startsWith('### ')) {
      current.rest.push(raw)
      continue
    }
    const attr = current.inIntro ? null : TOPIC_ATTR_LINE.exec(line)
    if (attr) {
      current.attrs[attr[1] as TopicAttr] = attr[2].trim()
      continue
    }
    if (!line && !current.inIntro) continue
    current.inIntro = true
    current.head.push(line)
  }
  flush()
  return { intro: intro.join(' '), topics }
}

/** Fenced languages rendered as structured blocks rather than code. */
export const BLOCK_LANGUAGES = ['cards', 'steps', 'callout', 'legend', 'guide', 'setup'] as const
export type BlockLanguage = (typeof BLOCK_LANGUAGES)[number]

export function isBlockLanguage(lang: string | undefined): lang is BlockLanguage {
  return (BLOCK_LANGUAGES as readonly string[]).includes(lang ?? '')
}
